import React from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

const StyledInput = styled.input`
  -webkit-font-smoothing: antialiased;
  ${props => props.inputType === "default" && css`
    outline: none;
    background: transparent;
    color: var(--gray);
    font-family: inherit;
    border: 0;
    padding: 0.6em 1.5em 0.5em;
    letter-spacing: 0.1em;
    width: 100%;
    -webkit-appearance: none;
    border-radius: 0;

    &::placeholder {
      color: var(--lighter-grey);
      text-transform: none;
      font-size: 0.9em;
      letter-spacing: 0;
    }
    &:focus {
      outline: none;
    }
  `}

  ${props => props.type === "currency" && css`
    padding: .6em 2em .5em;
  `}

  ${props => props.inputType === "registration" && css`
    outline: none;
    background: #fafafa;
    color: var(--gray);
    font-family: inherit;
    border: 0;
    font-size: 1.12em;
    padding: 1.2em;
    margin-top: 1.5em;
    letter-spacing: 0.1em;
    width: 100%;
    -webkit-appearance: none;
    border-radius: var(--card-radius);
    font-weight: 900;
    transition: 0.2s all ease-in;
    background: white;
    font-weight: 500;
    box-shadow: 0 4px 5px 0 rgba(0,0,0,0.1);
    &::placeholder {
      color: var(--lighter-gray);
      text-transform: none;
    }

    &:focus {
      background: var(--white);
      outline: none;
      box-shadow: var(--box-shadow);
    }
  `}

  ${props => props.fontSize && css`
    font-size: ${props.fontSize};
  `}

  ${props => props.transform && css`
    text-transform: ${props.transform};
  `}

  ${props => props.isDisabled && css`
    pointer-events: none;
  `}
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: ${props => inputWidth[props.width]};
  i {
    position: absolute;
    left: 18px;
  }
  .err-msg {
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 0;
    font-size: 0.8em;
    font-weight: 400;
    color: #cc0000;
    transform: translateY(50%);
    transition: 0.4s;
    padding-left: 2em;
    padding-top: .2em;
  }

  ${props => props.isInvalid && css`
    .err-msg {
      transform: translateY(100%);
      opacity: 1;
      transition: 0.4s;
    }
  `}
`;

const propTypes = {
  /** Placeholder for input */
  childIcon: PropTypes.element,

  /** Font size of text */
  fontSize: PropTypes.string,

  /** CSS text-transform for input text */
  inputType: PropTypes.oneOf(["default", "registration"]),

  /** Width of the input */
  isDisabled: PropTypes.bool,

  /** Theme of the input */
  isInvalid: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  /** If input is disabled */
  placeholder: PropTypes.string,

  /** The error message, if input is invalid */
  theme: PropTypes.oneOf(["light", "dark"]),

  /** Icon to prefix the input */
  transform: PropTypes.oneOf(["capitalize", "lowercase", "uppercase"]),

  /** Type of input */
  width: PropTypes.oneOf(["small", "normal", "medium", "large", "full"])
};

const defaultProps = {
  placeholder: "",
  fontSize: "1.12em",
  theme: "dark",
  width: "normal",
  inputType: "default"
};

const inputWidth = {
  small: "12em",
  normal: "15em",
  medium: "20em",
  large: "27em",
  full: "100%"
};

/**
 * One input box to rule them all
 */
const Input = (props) => {
  const {width, isInvalid} = props;
  return (
    <InputContainer isInvalid={isInvalid} width={width} >
      {props.childIcon}
      <StyledInput {...props} />
      <span className='err-msg'>{props.isInvalid}</span>
    </InputContainer>
  );
};

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

export default Input;
